import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  InputBase,
  Badge,
  Box,
  Menu,
  MenuItem,
  Button,
  Container,
  Avatar,
  Tooltip,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  Divider,
} from '@mui/material';
import { ListItemIcon, ListItemText } from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import StoreIcon from "@mui/icons-material/Store";
import InfoIcon from "@mui/icons-material/Info";
import ContactMailIcon from "@mui/icons-material/ContactMail";
import { styled, alpha } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import AccountCircle from '@mui/icons-material/AccountCircle';
import ShoppingCartCheckoutIcon from "@mui/icons-material/ShoppingCartCheckout";
import FavoriteIcon from '@mui/icons-material/Favorite';
import { MdOutlineShoppingBag } from "react-icons/md";
import LogoutIcon from '@mui/icons-material/Logout';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import logo from '../images/shaka-removebg-preview.png';
import { logout, syncLocalCart } from '../features/user/userSlice';

// --- Styled Components ---

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  background: 'rgba(5, 63, 92, 1)',
  backdropFilter: 'blur(12px)',
  WebkitBackdropFilter: 'blur(12px)',
  boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  transition: 'all 0.3s ease-in-out',
}));

const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: '24px',
  backgroundColor: alpha(theme.palette.common.white, 0.1),
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.white, 0.15),
    boxShadow: '0 0 0 1px rgba(255, 255, 255, 0.2)',
  },
  marginLeft: 0,
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  transition: 'all 0.3s ease',
  overflow: 'hidden',
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(1),
    width: 'auto',
  },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: alpha(theme.palette.common.white, 0.7),
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  width: '100%',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    fontSize: '0.9rem',
    [theme.breakpoints.up('sm')]: {
      width: '12ch',
      '&:focus': {
        width: '20ch',
      },
    },
    [theme.breakpoints.up('md')]: {
      width: '35ch',
      '&:focus': {
        width: '45ch',
      },
    },
  },
}));

const NavButtonContainer = styled(Box)({
  display: 'flex',
  gap: '8px',
  position: 'relative',
});

const NavLink = ({ children, onClick, active }) => (
  <Button
    onClick={onClick}
    sx={{
      color: active ? '#ff6701' : '#fff',
      textTransform: 'none',
      fontSize: '0.95rem',
      fontWeight: active ? 600 : 500,
      px: 2,
      position: 'relative',
      '&:hover': {
        color: '#ff6701',
        backgroundColor: 'transparent',
      },
      transition: 'color 0.3s ease',
    }}
  >
    {children}
    {active && (
      <motion.div
        layoutId="underline"
        style={{
          position: 'absolute',
          bottom: '4px',
          left: '20%',
          right: '20%',
          height: '2px',
          background: '#ff6701',
          borderRadius: '2px',
        }}
      />
    )}
  </Button>
);

const Header = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [anchorElUser, setAnchorElUser] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { isAuthenticated, user, cartProducts } = useSelector((state) => state.auth);

  useEffect(() => {
    setSearchTerm('');
  }, [location.pathname]);

  useEffect(() => {
    if (!isAuthenticated) {
      const localCart = JSON.parse(localStorage.getItem('cart')) || [];
      dispatch(syncLocalCart(localCart));
    }
  }, [isAuthenticated, dispatch]);

  const cartItemCount = cartProducts?.length || 0;

  const handleSearch = () => {
    if (searchTerm.trim()) {
      navigate(`/product?search=${encodeURIComponent(searchTerm)}`);
      setSearchTerm('');
    }
  };

  const handleUserMenuOpen = (event) => setAnchorElUser(event.currentTarget);
  const handleUserMenuClose = () => setAnchorElUser(null);
  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const handleLogout = () => {
    handleUserMenuClose();
    localStorage.removeItem('customer');
    localStorage.removeItem('cart');
    dispatch(logout());
    navigate('/');
  };

  const getProfilePath = () => (user?._id ? `/profile/${user._id}` : '/login');

  const handleNav = (path) => {
    navigate(path);
    if (mobileOpen) setMobileOpen(false);
  };

  const navItems = [
    { label: 'Home', path: '/' },
    { label: 'Shop', path: '/product' },
    { label: 'About', path: '/about' },
    { label: 'Contact', path: '/contact' },
  ];

  return (
    <>
      <StyledAppBar position="sticky">
        <Container maxWidth="xl">
          <Toolbar
            disableGutters
            sx={{
              height: { xs: 70, md: 80 },
              justifyContent: 'space-between',
            }}
          >
            {/* Logo */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                gap: 1.5,
              }}
              onClick={() => handleNav('/')}
            >
              <motion.img
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                src={logo}
                alt="Logo"
                style={{
                  width: 45,
                  height: 45,
                  borderRadius: '50%',
                  boxShadow: '0 4px 10px rgba(0,0,0,0.2)',
                }}
              />
              <Typography
                variant="h6"
                noWrap
                sx={{
                  display: { xs: 'none', sm: 'block' },
                  fontFamily: "'Outfit', sans-serif",
                  fontWeight: 800,
                  fontSize: '1.7rem',
                  letterSpacing: '-0.5px',
                  color: '#ff6701',
                  background: 'linear-gradient(to right, #ff6701, #febd69)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  transition: 'all 0.3s ease',
                }}
              >
                Shakambari
              </Typography>
            </Box>

            {/* Desktop Navigation */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, ml: 4, flexGrow: 1 }}>
              <NavButtonContainer>
                {navItems.map((item) => (
                  <NavLink
                    key={item.label}
                    onClick={() => handleNav(item.path)}
                    active={location.pathname === item.path}
                  >
                    {item.label}
                  </NavLink>
                ))}
              </NavButtonContainer>
            </Box>

            {/* Search and Icons */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1.5 } }}>
              <Search>
                <SearchIconWrapper>
                  <SearchIcon fontSize="small" />
                </SearchIconWrapper>
                <StyledInputBase
                  placeholder="Search products..."
                  inputProps={{ 'aria-label': 'search' }}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                />
              </Search>

              {/* Utility Icons */}
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Tooltip title="Wishlist">
                  <IconButton
                    onClick={() => navigate('/wishlist')}
                    sx={{ color: '#ff4d4d', '&:hover': { background: alpha('#ff4d4d', 0.1) } }}
                  >
                    <FavoriteIcon fontSize="medium" />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Cart">
                  <IconButton
                    onClick={() => navigate('/cart')}
                    sx={{ color: '#fff', '&:hover': { background: alpha('#fff', 0.1) } }}
                  >
                    <Badge
                      badgeContent={cartItemCount}
                      color="error"
                      sx={{ '& .MuiBadge-badge': { background: '#ff6701' } }}
                    >
                      <MdOutlineShoppingBag size={24} />
                    </Badge>
                  </IconButton>
                </Tooltip>

                {isAuthenticated && user?.email ? (
                  <Tooltip title={user.email}>
                    <IconButton onClick={handleUserMenuOpen} sx={{ p: 0.5, ml: 1 }}>
                      <Avatar
                        sx={{
                          width: 34,
                          height: 34,
                          bgcolor: '#ff6701',
                          fontSize: '0.9rem',
                          fontWeight: 600,
                          border: '2px solid rgba(255,255,255,0.2)',
                        }}
                      >
                        {user.email.charAt(0).toUpperCase()}
                      </Avatar>
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Tooltip title="Login">
                    <IconButton
                      onClick={() => navigate('/login')}
                      sx={{ color: '#fff', ml: 1, '&:hover': { background: alpha('#fff', 0.1) } }}
                    >
                      <AccountCircle fontSize="large" />
                    </IconButton>
                  </Tooltip>
                )}

                {/* Mobile Menu Toggle */}
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  edge="end"
                  onClick={handleDrawerToggle}
                  sx={{ display: { md: 'none' }, ml: 0.5 }}
                >
                  <MenuIcon />
                </IconButton>
              </Box>
            </Box>

            {/* Profile Menu */}
            <Menu
              anchorEl={anchorElUser}
              open={Boolean(anchorElUser)}
              onClose={handleUserMenuClose}
              PaperProps={{
                sx: {
                  mt: 1.5,
                  minWidth: 180,
                  borderRadius: 2,
                  boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                  overflow: 'visible',
                  '&:before': {
                    content: '""',
                    display: 'block',
                    position: 'absolute',
                    top: 0,
                    right: 14,
                    width: 10,
                    height: 10,
                    bgcolor: 'background.paper',
                    transform: 'translateY(-50%) rotate(45deg)',
                    zIndex: 0,
                  },
                },
              }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              <MenuItem onClick={() => { handleUserMenuClose(); navigate(getProfilePath()); }}>
                <ListItemIcon><AccountCircleIcon fontSize="small" /></ListItemIcon>
                Profile
              </MenuItem>
              <MenuItem onClick={() => { handleUserMenuClose(); navigate('/orders'); }}>
                <ListItemIcon><ShoppingCartCheckoutIcon fontSize="small" /></ListItemIcon>
                Orders
              </MenuItem>
              <Divider sx={{ my: 1 }} />
              <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
                <ListItemIcon><LogoutIcon fontSize="small" color="error" /></ListItemIcon>
                Logout
              </MenuItem>
            </Menu>
          </Toolbar>
        </Container>
      </StyledAppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        PaperProps={{
          sx: {
            width: '80%',
            maxWidth: 320,
            background: '#053f5c',
            color: '#fff',
          },
        }}
      >
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <img src={logo} alt="Logo" style={{ width: 35, height: 35, borderRadius: '50%' }} />
            <Typography 
              variant="h6" 
              sx={{ 
                color: '#ff6701', 
                fontWeight: 800,
                fontFamily: "'Outfit', sans-serif",
              }}
            >
              Shakambari
            </Typography>
          </Box>
          <IconButton onClick={handleDrawerToggle} sx={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
        <List sx={{ mt: 2 }}>
          {navItems.map((item) => (
            <ListItem key={item.label} disablePadding>
              <ListItemButton
                onClick={() => handleNav(item.path)}
                sx={{
                  py: 2,
                  px: 3,
                  color: location.pathname === item.path ? '#ff6701' : '#fff',
                }}
              >
                <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                  {item.label === 'Home' && <HomeIcon />}
                  {item.label === 'Shop' && <StoreIcon />}
                  {item.label === 'About' && <InfoIcon />}
                  {item.label === 'Contact' && <ContactMailIcon />}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{ fontWeight: location.pathname === item.path ? 700 : 500 }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Drawer>
    </>
  );
};

export default Header;
